import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { asMutable } from 'modules/immutable';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { updateDynamicReportColumns } from '../../../../actions';
import { getColumnDefinitionByKind, TemplateReference } from '../../../../config/standard/columnDefinitions';
import ColumnKind from '../../../../config/standard/columnKind';
import { createDefaultColumns } from '../../../../data/reportConfig';
import { SectionReportTypeRequestProps } from '../SectionReportTypeRequest/SectionReportTypeRequest.types';
import { excludedIntegrationCodes } from './SectionReportTypeLineItem.constants';

export const useSectionReportTypeLineItem = (
    reportConfig: SectionReportTypeRequestProps['reportConfig'],
    onReportConfigChange: SectionReportTypeRequestProps['onReportConfigChange']
) => {
    const dispatch = useDispatch();

    const company = useCompanyBasedOnUrl();

    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));
    const companyTemplates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));

    const templateColumn = useMemo(
        () => reportConfig.columns.find((column) => column.kind === ColumnKind.TemplateId)!,
        [reportConfig.columns]
    );

    const templateOptions = useSelector(
        (state) =>
            getColumnDefinitionByKind(ColumnKind.TemplateId, company).getOptions!(
                state,
                templateColumn,
                company.id,
                domain.ReportType.LineItem
            ) as TemplateReference[]
    );

    const integrationTemplates = useMemo(
        () =>
            templateOptions.filter((template) => {
                if (!template.integrationCode) {
                    return false;
                }

                if (excludedIntegrationCodes.includes(template.integrationCode)) {
                    return false;
                }

                const isAirwallex = template.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment;

                if (!isAirwallex) {
                    return true;
                }

                const airwallexTemplate = companyTemplates.find(
                    (companyTemplate) =>
                        companyTemplate.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment
                );

                if (airwallexTemplate) {
                    const wasActive = Boolean(airwallexTemplate.version);

                    if (airwallexTemplate.enabled || wasActive) {
                        return true;
                    }
                }

                return false;
            }),
        [companyTemplates, templateOptions]
    );

    const handleTemplateToggle = useCallback(
        (value: domain.ReportCode) => {
            const newReportConfig = asMutable(reportConfig, { deep: true });

            newReportConfig.reportCode = value;
            newReportConfig.columns = createDefaultColumns(reportConfig.companyId, domain.ReportType.LineItem, value);

            onReportConfigChange(newReportConfig);

            if (integration?.integrationType === domain.IntegrationType.Xero) {
                dispatch(updateDynamicReportColumns({ reportConfig: newReportConfig }));
            }
        },
        [dispatch, integration?.integrationType, onReportConfigChange, reportConfig]
    );

    return {
        integration,
        integrationTemplates,
        handleTemplateToggle,
    };
};
